// The corkboard (CorkboardView on the Mac): index cards for a folder's children, laid out as the Layout menu says —
// a grid (a part's chapters side by side, each a column of its scenes), freeform (views/plan/FreeformBoard.tsx) or
// swimlanes (views/plan/SwimlaneBoard.tsx) — tinted by the revision heatmap, with snowflakes on cold scenes. On a phone
// it's the Mac's iPhone page (views/plan/PhonePlan.tsx). A folder's card goes into it; a scene's card opens it in Write.
//
// Editing (the grid): drag a card before or after another to reorder the folder (a bar shows where), within a chapter's
// column or across to another, or into the space below a column's cards to put it last; New Scene / New Folder go after
// the selected card, else at the folder's end; the context menu (or ⇧F10) has Rename, Convert, Status and Label, Move to
// Trash. In a collection the board reads only (Status and Label still change). Each is one edit of the project.
//
// Outside the story's scenes (Research & Notes, Publishing: `planShowsScenes`) the board counts documents and has no
// swimlanes, heatmap or cold marks; a character's study page there is a cast card, whose menu opens the character study
// or the character in Cast & Places.
import { useMemo, useRef, useState, type DragEvent } from 'react';
import type { OpenedProject, TreeNode } from '@writing-suite/core';
import { alternateSetForNode, columnTitle, planShowsScenes } from '@writing-suite/core/plan';
import { alternateSets } from '@writing-suite/core';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { followLink } from '../shell/route.ts';
import { RenameField } from '../shell/ContentsTree.tsx';
import { structureEdits, useWorkspace, type Workspace } from '../shell/workspace.ts';
import { PREF, useSharedPreference, type CorkboardLayout } from '../store/plan.ts';
import { boardFor, formatNumber, readingFragments } from './helpers.ts';
import { PresenceDots } from './Presence.tsx';
import { MenuPopup } from './Menu.tsx';
import { dropPlacement } from './structure.ts';
import { FreeformBoard } from './plan/FreeformBoard.tsx';
import { usePhone, usePlanHeat, type PlanHeat } from './plan/hooks.ts';
import { CardFace, castCardMenu, heatClass } from './plan/IndexCard.tsx';
import { planItemMenu } from './plan/menus.ts';
import { filterMenuEntries } from './FilterBar.tsx';
import { PhonePlan } from './plan/PhonePlan.tsx';
import { SwimlaneBoard } from './plan/SwimlaneBoard.tsx';
import { documentNoun, nounCount } from '../store/binderFilter.ts';
import { journalPostsInTree } from '@writing-suite/core';
import { useCorkboardLayout } from './plan/hooks.ts';
import { PostBoard } from './plan/PostBoard.tsx';
import { isJournalPosts, plannedPost, PostHeader, PostOpeningCard, PostPlanEmpty } from './plan/posts.tsx';

export { cardFacts } from './plan/IndexCard.tsx';

const findNode = (nodes: TreeNode[], id: string): TreeNode | null => {
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = findNode(n.children, id);
    if (inner) return inner;
  }
  return null;
};

/** Dim Others: a card the contents' filter leaves out, faded where it is (a leaf's own fragment decides). */
export function fadedCard(ws: Workspace | null, n: TreeNode): string {
  const filter = ws?.filter;
  return filter?.view && n.children.length === 0 && n.fragmentId && !filter.matchesFragment(n.fragmentId) ? ' filtered-out' : '';
}

/** The structure edits over the tree the board shows: the whole arrangement when it dims (no placement to translate). */
export function dimmed(ws: Workspace, tree: TreeNode[]): Workspace {
  return ws.fullTree === tree ? { ...ws, tree } : ws;
}

/** "Part Two · 8 scenes": where the cards are and how many scenes they hold ("Characters · 12 documents" outside the
 *  story's scenes, posts in a journal). */
export function boardHint(props: Pick<PageProps, 'tree' | 'node' | 'arrangement'> & { project?: PageProps['project'] }, layout: CorkboardLayout, editable: boolean): string {
  const journal = (props.project as Partial<OpenedProject> | undefined)?.record?.projectType === 'journal';
  const posts = journal && props.arrangement.role === 'manuscript';
  const planned = posts ? plannedPost(props.tree, props.node?.id ?? null) : null;
  const board = boardFor(props.tree, props.node?.id ?? null, posts);
  const leaves = (nodes: TreeNode[]): number => nodes.reduce((n, t) => n + (t.children.length === 0 ? 1 : leaves(t.children)), 0);
  // A journal's posts count as posts, not by the sections under them; a post's plan counts its sections.
  const count = layout === 'swimlanes' ? leaves(props.tree)
    : planned ? board.cards.length
    : posts ? journalPostsInTree(layout === 'board' ? props.tree : board.cards).length
    : leaves(board.cards);
  const place = layout === 'swimlanes' || layout === 'board' ? props.arrangement.name : (board.folder?.title ?? props.arrangement.name);
  const noun = documentNoun(journal ? 'journal' : null, planShowsScenes(props.arrangement.role));
  const parts = [place, planned ? (count === 1 ? '1 section' : `${formatNumber(count)} sections`) : nounCount(noun, count)].filter(Boolean);
  if (layout !== 'swimlanes' && editable) parts.push('drag to reorder');
  return parts.join(' · ');
}

export function Corkboard(given: PageProps) {
  const phone = usePhone();
  const ws = useWorkspace();
  // Dim Others: the whole arrangement, the cards the filter leaves out faded (`filtered-out`) where they are.
  const [dims] = useSharedPreference(PREF.dimsFilteredOut, false);
  const dimming = dims && !!ws?.filter?.view && !!ws.fullTree;
  const props = useMemo(() => {
    if (!dimming) return given;
    const node = given.node ? findNode(ws!.fullTree!, given.node.id) : null;
    return { ...given, tree: ws!.fullTree!, node };
  }, [given, dimming, ws?.fullTree]); // eslint-disable-line react-hooks/exhaustive-deps
  // Research, notes and book matter aren't scenes: no heatmap, cold marks or swimlanes for them.
  const scenes = planShowsScenes(props.arrangement.role);
  const { shown: layout, planned } = useCorkboardLayout(props.project, props.arrangement, props.tree, props.node?.id ?? null);
  const heat = usePlanHeat(props.project, props.tree, scenes);
  if (phone) return <PhonePlan {...props} outline={false} heat={heat} />;
  // A journal post's plan: its header above its sections.
  const head = planned ? <PostHeader {...props} post={planned.post} node={planned.node} /> : null;
  if (layout === 'board') return <PostBoard {...props} />;
  if (layout === 'freeform') return <>{head}<FreeformBoard {...props} heat={heat} /></>;
  if (layout === 'swimlanes') return <SwimlaneBoard {...props} />;
  return <>{head}<GridBoard {...props} heat={heat} /></>;
}

function GridBoard({ project, tree, node, hrefFor, heat, arrangement }: PageProps & { heat: PlanHeat }) {
  const ws = useWorkspace();
  const editable = !!ws?.canStructure;
  const posts = isJournalPosts(project, arrangement);
  const planned = posts ? plannedPost(tree, node?.id ?? null) : null;
  const postIds = posts ? new Set(journalPostsInTree(tree).map((p) => p.nodeId)) : null;
  const board = boardFor(tree, node?.id ?? null, posts);
  const fields = project.fields();
  const entities = project.entities();
  const sets = useMemo(() => {
    const p = project as OpenedProject;
    return p.record ? alternateSets(p.record) : [];
  }, [project]);
  const openers = useMemo(() => project.openers(), [project]);
  const heading = board.folder ? board.folder.title || 'Untitled' : ws?.arrangement.role === 'collection' ? ws.arrangement.name : 'Top level';
  const [menu, setMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const dragging = useRef<string | null>(null);
  const [over, setOver] = useState<{ id: string; side: 'before' | 'after' | 'end' } | null>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const columns = !posts && board.cards.length > 0 && board.cards.every((n) => n.children.length > 0);

  const sideOf = (e: DragEvent): 'before' | 'after' => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // A column of cards reads top to bottom; a grid left to right.
    return columns ? (e.clientY - r.top < r.height / 2 ? 'before' : 'after') : e.clientX - r.left < r.width / 2 ? 'before' : 'after';
  };

  const newItem = (folder: boolean) => {
    if (!ws?.live) return;
    // A journal's post goes first in its folder, as a draft (New Post).
    if (posts && !folder) {
      const made = structureEdits(ws).newItem(board.selected ?? board.folder?.id ?? null, false);
      if (made) ws.setRenaming(made, 'board');
      return;
    }
    // After the selected card, else at the end of the folder shown.
    const placement = board.selected ? { after: board.selected } : { inside: board.folder?.id ?? null };
    const made = ws.live.edit((e) => e.newFragment({ arrangementId: ws.arrangement.id, placement, title: folder ? 'New Folder' : 'Untitled', isFolder: folder }));
    ws.setRenaming(made.nodeId, 'board');
  };

  const focusCard = (id: string) => requestAnimationFrame(() => cardsRef.current?.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(id)}"] a`)?.focus());

  const card = (n: TreeNode) => {
    // A journal's post opens its plan; a folder goes into it; a scene (or a post's section) opens in Write.
    const folder = postIds?.has(n.id) || n.isFolder || n.children.length > 0;
    const href = folder ? hrefFor('plan/corkboard', n.id) : hrefFor('write', n.id);
    const drop = over?.id === n.id ? ` drop-${over.side}` : '';
    const renaming = ws?.renaming === n.id && ws.renamingIn === 'board';
    return (
      <li
        key={n.id}
        className={`card${n.id === board.selected ? ' selected' : ''}${drop}${heatClass(heat.heat[n.id])}${fadedCard(ws, n)}`}
        data-node-id={n.id}
        data-heat={heat.heat[n.id] || undefined}
        draggable={editable && !renaming}
        onDragStart={(e) => {
          dragging.current = n.id;
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', n.title || 'Untitled');
          if (n.fragmentId) e.dataTransfer.setData('application/x-writing-suite-fragments', JSON.stringify([n.fragmentId]));
        }}
        onDragEnd={() => {
          dragging.current = null;
          setOver(null);
        }}
        onDragOver={(e) => {
          const moving = dragging.current;
          if (!moving || moving === n.id) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
          const side = sideOf(e);
          if (over?.id !== n.id || over.side !== side) setOver({ id: n.id, side });
        }}
        onDragLeave={(e) => {
          if (over?.id === n.id && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
        }}
        onDrop={(e) => {
          const moving = dragging.current;
          dragging.current = null;
          setOver(null);
          if (!moving || !ws || moving === n.id) return;
          e.preventDefault();
          e.stopPropagation();
          const placement = dropPlacement(tree, [moving], n.id, sideOf(e));
          if (placement) structureEdits(dimmed(ws, tree)).move([moving], placement);
          focusCard(moving);
        }}
        onContextMenu={(e) => {
          if (!ws) return;
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, id: n.id });
        }}
        onKeyDown={(e) => {
          if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setMenu({ x: r.left + 24, y: r.top + 32, id: n.id });
            e.preventDefault();
          } else if (e.key === 'F2' && editable && n.fragmentId) {
            ws?.setRenaming(n.id, 'board');
            e.preventDefault();
          }
        }}
      >
        {renaming ? (
          <div className="card-rename">
            <RenameField
              title={n.title}
              className="rename-field card-rename-field"
              onDone={(title) => {
                if (title !== null && ws) structureEdits(ws).rename(n.id, title);
                ws?.setRenaming(null);
                focusCard(n.id);
              }}
            />
          </div>
        ) : (
          <a href={href} draggable={false} onClick={(e) => followLink(e, href)} aria-current={n.id === board.selected ? 'true' : undefined}>
            <CardFace
              project={project}
              node={postIds?.has(n.id) ? { ...n, children: [] } : n}
              fields={fields}
              entities={entities}
              look={{ heat: heat.heat[n.id], cold: heat.cold(n), coldDays: heat.coldDays, alternates: alternateSetForNode(sets, n.id)?.members.length }}
            />
            <PresenceDots fragmentIds={readingFragments([n])} />
          </a>
        )}
      </li>
    );
  };

  return (
    <div className="corkboard-page" data-layout="grid">
      {planned ? null : <header className="page-head">
        <h1 tabIndex={-1}>{heading}</h1>
        <p className="page-sub">{board.cards.length === 1 ? '1 card' : `${board.cards.length} cards`}</p>
        {editable ? (
          <span className="page-actions">
            <button type="button" className="quiet-button" onClick={() => newItem(false)}>
              {posts ? 'New post' : 'New scene'}
            </button>
            <button type="button" className="quiet-button" onClick={() => newItem(true)}>
              New folder
            </button>
          </span>
        ) : ws?.arrangement.role === 'collection' ? (
          <p className="read-only-note">A collection: its documents in reading order. Reorder them in the manuscript.</p>
        ) : null}
      </header>}
      <div ref={cardsRef}>
        {board.cards.length === 0 ? (
          planned ? <PostPlanEmpty post={planned.post} /> : <p className="empty-note">Nothing in this folder.</p>
        ) : columns ? (
          <div className="chapter-columns" role="list" aria-label={`Chapters in ${heading}`}>
            {board.cards.map((chapter) => {
              const title = columnTitle(chapter.title || 'Untitled', chapter.fragmentId ? openers.get(chapter.fragmentId) : null);
              const words = project.subtreeWords(chapter);
              return (
                <section key={chapter.id} className="chapter-column" role="listitem" data-node-id={chapter.id} aria-label={title}>
                  <h2 className="chapter-column-title">
                    <a href={hrefFor('plan/corkboard', chapter.id)} onClick={(e) => followLink(e, hrefFor('plan/corkboard', chapter.id))}>
                      {title}
                    </a>
                    <span className="chapter-column-words">{words === 1 ? '1 word' : `${formatNumber(words)} words`}</span>
                  </h2>
                  <ul className="cards column-cards" aria-label={`Cards in ${chapter.title || 'Untitled'}`}>
                    {chapter.children.map(card)}
                  </ul>
                  {editable ? (
                    <div
                      className={`column-end${over?.id === chapter.id ? ' drop-end' : ''}`}
                      data-column-end={chapter.id}
                      aria-hidden="true"
                      onDragOver={(e) => {
                        const moving = dragging.current;
                        if (!moving || moving === chapter.id) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (over?.id !== chapter.id) setOver({ id: chapter.id, side: 'end' });
                      }}
                      onDragLeave={() => over?.id === chapter.id && setOver(null)}
                      onDrop={(e) => {
                        const moving = dragging.current;
                        dragging.current = null;
                        setOver(null);
                        if (!moving || !ws || moving === chapter.id) return;
                        e.preventDefault();
                        structureEdits(ws).move([moving], { inside: chapter.id });
                        focusCard(moving);
                      }}
                    />
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : (
          <ul className="cards" aria-label={`Cards in ${heading}`}>
            {planned && project.words(planned.post.fragmentId) > 0 ? <PostOpeningCard project={project} post={planned.post} hrefFor={hrefFor} /> : null}
            {board.cards.map(card)}
          </ul>
        )}
      </div>
      {menu && ws ? (
        <MenuPopup
          label="Card"
          at={menu}
          entries={(() => {
            const n = findNode(tree, menu.id);
            return (n && castCardMenu({ project, hrefFor }, n))
              ?? [
                ...planItemMenu(ws, [menu.id], { onRename: (id) => ws.setRenaming(id, 'board'), onCreated: (id) => ws.setRenaming(id, 'board') }),
                ...filterMenuEntries(ws, menu.id),
              ];
          })()}
          onClose={(restore) => {
            const id = menu.id;
            setMenu(null);
            if (restore) focusCard(id);
          }}
        />
      ) : null}
    </div>
  );
}
